/**
 * Natural Language Search Patterns
 * Matches common salon queries without AI.
 * Returns a structured action that the search UI can execute.
 */
import moment from 'moment';
import { can, canMarket, isSelfEmployedMe } from '../permissions/access';
import { isHidden as isReceptionHidden } from '../../_utils/receptionMode';

// Reception mode: on a locked front desk, shortcuts into areas its role hides
// aren't offered (the routes and the API refuse them anyway).
const PATH_AREAS = [
  [/^\/finance/, ['transactions']],
  [/^\/staff/, ['staff']],
  [/^\/clients/, ['clients']],
  [/^\/new-services/, ['catalogue', 'services']],
  [/^\/marketing/, ['marketing']],
  [/^\/settings/, ['settings']],
  [/^\/forms/, ['catalogue', 'forms']],
];
const ACTION_AREAS = { clientHistory: ['clients', 'clientDetails'] };
export function receptionAllows(item) {
  if (!item) return true;
  const areas = (ACTION_AREAS[item.action] || []).concat(...PATH_AREAS.filter(([re]) => item.path && re.test(item.path)).map(([, a]) => a));
  return !areas.length || !isReceptionHidden(areas);
}

/**
 * Who a destination is for — the same rules as the side nav and the route
 * guards (permissions/access), so search never offers a page that would
 * only say "You don't have access to this". The owner passes every one.
 */
const NEEDS = {
  '/finance': () => isSelfEmployedMe() || can('reports'),
  '/staff': () => isSelfEmployedMe() || can('team'),
  '/new-services': () => isSelfEmployedMe() || can('services'),
  '/marketing-campaigns': () => canMarket(),
  '/forms': () => can('services'),
};
export function allowedPath(path) {
  const rule = NEEDS[path];
  try { return !rule || !!rule(); } catch (e) { return true; }
}

const PATTERNS = [
  // Cancellations
  { match: /who.*cancel.*today/i, label: 'Cancellations today', action: 'bookings', filter: { status: 3, startDate: moment().format('YYYY-MM-DD'), endDate: moment().format('YYYY-MM-DD') } },
  { match: /who.*cancel.*this week/i, label: 'Cancellations this week', action: 'bookings', filter: { status: 3, startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'), endDate: moment().endOf('isoWeek').format('YYYY-MM-DD') } },
  { match: /who.*cancel.*this month/i, label: 'Cancellations this month', action: 'bookings', filter: { status: 3, startDate: moment().startOf('month').format('YYYY-MM-DD'), endDate: moment().endOf('month').format('YYYY-MM-DD') } },
  { match: /cancel/i, label: 'Cancellations', action: 'bookings', filter: { status: 3 } },

  // No shows
  { match: /no.?show.*today/i, label: 'No shows today', action: 'bookings', filter: { status: 6, startDate: moment().format('YYYY-MM-DD'), endDate: moment().format('YYYY-MM-DD') } },
  { match: /no.?show.*this week/i, label: 'No shows this week', action: 'bookings', filter: { status: 6, startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'), endDate: moment().endOf('isoWeek').format('YYYY-MM-DD') } },
  { match: /no.?show.*this month/i, label: 'No shows this month', action: 'bookings', filter: { status: 6, startDate: moment().startOf('month').format('YYYY-MM-DD'), endDate: moment().endOf('month').format('YYYY-MM-DD') } },
  { match: /no.?show/i, label: 'No shows', action: 'bookings', filter: { status: 6 } },

  // Diary navigation — :salonId is resolved at click time (see resolvePath
  // in UniversalSearch.js); the bare /diary route doesn't exist, DiaryV2 is
  // only mounted at /diary/:salonId, so this used to 404 into the app shell.
  { match: /booking.*today|today.*booking|who.*booked.*today|today.*diary/i, label: 'Today\'s diary', action: 'navigate', path: '/diary/:salonId' },
  { match: /booking.*tomorrow|tomorrow/i, label: 'Tomorrow\'s diary', action: 'navigate', path: '/diary/:salonId', dateOffset: 1 },
  { match: /booking.*yesterday|yesterday/i, label: 'Yesterday\'s diary', action: 'navigate', path: '/diary/:salonId', dateOffset: -1 },
  { match: /next week/i, label: 'Next week', action: 'navigate', path: '/diary/:salonId', dateOffset: 7 },

  // Finance
  { match: /how much.*today|revenue.*today|sales.*today|takings.*today/i, label: 'Today\'s revenue', action: 'navigate', path: '/finance' },
  { match: /how much.*this week|revenue.*this week|sales.*this week/i, label: 'This week\'s revenue', action: 'navigate', path: '/finance' },
  { match: /finance|revenue|sales|money|income|takings/i, label: 'Finance overview', action: 'navigate', path: '/finance' },

  // Staff
  { match: /who.*working.*today|staff.*today|team.*today|rota.*today/i, label: 'Who\'s working today', action: 'navigate', path: '/diary/:salonId' },
  { match: /who.*working.*tomorrow|staff.*tomorrow/i, label: 'Who\'s working tomorrow', action: 'navigate', path: '/diary/:salonId', dateOffset: 1 },
  { match: /schedule|rota|shift/i, label: 'Schedules & Hours', action: 'navigate', path: '/staff' },

  // Upcoming
  { match: /upcoming.*booking|upcoming.*appointment/i, label: 'Upcoming bookings', action: 'bookings', filter: { status: 1 } },
  { match: /completed.*booking|completed/i, label: 'Completed bookings', action: 'bookings', filter: { status: 4 } },

  // Client history (captures name)
  { match: /(.+?)(?:'s|s)\s+(?:last|recent|previous|history|booking)/i, label: null, action: 'clientHistory', nameGroup: 1 },
  { match: /(?:book|appointment)\s+(?:for|with)\s+(.+)/i, label: null, action: 'bookFor', nameGroup: 1 },
];

/**
 * Match a search query against natural language patterns.
 * Returns { label, action, ...params } or null if no match.
 */
export function matchNaturalLanguage(query) {
  if (!query || query.length < 3) return null;

  for (const pattern of PATTERNS) {
    if (pattern.path && !allowedPath(pattern.path)) continue;
    const match = query.match(pattern.match);
    if (match) {
      const result = { ...pattern };
      delete result.match;

      // Extract captured name if pattern has nameGroup
      if (pattern.nameGroup && match[pattern.nameGroup]) {
        result.extractedName = match[pattern.nameGroup].trim();
        if (!result.label) {
          if (result.action === 'clientHistory') {
            result.label = `${result.extractedName}'s booking history`;
          } else if (result.action === 'bookFor') {
            result.label = `Book appointment for ${result.extractedName}`;
          }
        }
      }

      return receptionAllows(result) ? result : null;
    }
  }
  return null;
}

/**
 * Quick actions - static commands always available.
 *
 * All of these are plain page navigations now — every path is a route that
 * actually exists (verified against routes.js). The four "New X" actions
 * used to push to the diary and fire a `beu-search-action` custom event for
 * a FAB to pick up; nothing ever listened for it, and "New Client" pushed a
 * `?add=true` query param nothing read either, so all four silently landed
 * on a plain list/diary and did nothing further. They now go straight to
 * the real dedicated create pages those flows already have.
 * :salonId is resolved at click time in UniversalSearch.js's resolvePath.
 */
export const QUICK_ACTIONS = [
  { id: 'new-booking', label: 'New Booking', keywords: ['book', 'appointment', 'schedule', 'create booking'], icon: 'booking', action: 'navigate', path: '/newbooking/:salonId' },
  { id: 'new-event', label: 'New Event', keywords: ['event', 'block', 'busy', 'create event'], icon: 'event', action: 'navigate', path: '/event/:salonId' },
  { id: 'new-leave', label: 'New Leave', keywords: ['leave', 'holiday', 'vacation', 'time off', 'day off'], icon: 'leave', action: 'navigate', path: '/leave/:salonId' },
  { id: 'new-client', label: 'New Client', keywords: ['client', 'customer', 'add client', 'create client'], icon: 'client', action: 'navigate', path: '/clients/add' },
  { id: 'go-diary', label: 'Go to Diary', keywords: ['diary', 'calendar', 'schedule', 'appointments'], icon: 'diary', action: 'navigate', path: '/diary/:salonId' },
  { id: 'go-clients', label: 'Go to Clients', keywords: ['clients', 'customers', 'client list'], icon: 'clients', action: 'navigate', path: '/clients' },
  { id: 'go-finance', label: 'Go to Finance', keywords: ['finance', 'revenue', 'money', 'payments', 'transactions'], icon: 'finance', action: 'navigate', path: '/finance' },
  { id: 'go-team', label: 'Go to Team', keywords: ['team', 'staff', 'members', 'employees'], icon: 'team', action: 'navigate', path: '/staff' },
  { id: 'go-catalogue', label: 'Go to Catalogue', keywords: ['catalogue', 'catalog', 'services', 'products', 'inventory'], icon: 'catalogue', action: 'navigate', path: '/new-services' },
  { id: 'go-marketing', label: 'Go to Marketing', keywords: ['marketing', 'communications', 'reminders', 'sms', 'email'], icon: 'marketing', action: 'navigate', path: '/marketing-campaigns' },
  { id: 'go-settings', label: 'Go to Settings', keywords: ['settings', 'preferences', 'config', 'account'], icon: 'settings', action: 'navigate', path: '/settings' },
  { id: 'go-forms', label: 'Go to Forms', keywords: ['forms', 'consultation', 'waiver', 'intake'], icon: 'forms', action: 'navigate', path: '/forms' },
];

/**
 * Filter quick actions by query
 */
export function filterActions(query) {
  const actions = QUICK_ACTIONS.filter(a => allowedPath(a.path) && receptionAllows(a));
  if (!query) return actions.slice(0, 5);
  const lower = query.toLowerCase();
  return actions.filter(a =>
    a.label.toLowerCase().includes(lower) ||
    a.keywords.some(k => k.includes(lower))
  ).slice(0, 5);
}

/**
 * Recent items - stored in localStorage
 */
const RECENT_KEY = 'beu_recent_search';
const MAX_RECENT = 10;

export function getRecentItems() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch { return []; }
}

export function addRecentItem(item) {
  const recent = getRecentItems().filter(r => !(r.type === item.type && r.id === item.id));
  recent.unshift(item);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}
